import { track } from '@tldraw/tldraw'
import { observer } from 'mobx-react-lite'
import { Section } from './Section'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './ColorsPanel.module.scss'
import { Color, SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { ColorSwatch } from '../ColorSwatch/ColorSwatch'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { isEqual } from 'lodash'
import { useRootStore } from 'stores/RootStore'
import { BrandColors } from './BrandColors'
import { SOLID_COLORS } from './constants'
import { ColorsGrid } from './ColorsGrid'
import { SolidFill } from 'components/VisualStudioPlayground/FillColor/SolidFill'
import { HexColorInput } from 'components/HexColorInput/HexColorInput'

interface Props {
  allowTransparency?: boolean
  selectedColor: SolidColor
  documentSolidColors: SolidColor[]
  onSelect: (color: Color) => void
  onNewColorChange: (color: Color) => void
}

export const SolidColorsPanelBody = track(
  observer(({ allowTransparency, selectedColor, documentSolidColors, onSelect, onNewColorChange }: Props) => {
    const { designStore } = useRootStore()
    const { isMobile } = useResponsiveDesign()

    const handleChange = (color: Color) => {
      const colorExsistsInFile = documentSolidColors.find((c) => isEqual(c, color))
      if (colorExsistsInFile) {
        onSelect(color)
      } else {
        onNewColorChange(color)
      }
    }

    const handleHexInput = (value: string) => {
      if (!value.startsWith('#')) {
        value = `#${value}`
      }
      handleChange({ ...selectedColor, color: value })
    }

    return (
      <div className={Styles.sections}>
        <Section.Root>
          <Section.Header>
            <Text variant="smallList">Other colors in the file</Text>
          </Section.Header>

          <div className={Styles.colorsGrid}>
            {documentSolidColors.map((color, index) => (
              <div key={index} className={Styles.colorGridItem}>
                <ColorSwatch
                  size={isMobile ? 42 : 30}
                  color={color}
                  isSelected={isEqual(color, selectedColor)}
                  onPress={() => {
                    onSelect(color)
                  }}
                />
              </div>
            ))}
          </div>
        </Section.Root>

        {designStore?.brandVisualKitId && (
          <BrandColors id={designStore?.brandVisualKitId} selectedColor={selectedColor} onSelect={onSelect} />
        )}

        <Section.Root>
          <Section.Header>
            <Text variant="smallList">Solid Colors</Text>
          </Section.Header>

          <ColorsGrid
            colors={SOLID_COLORS}
            selectedColor={selectedColor}
            onSelect={(color) => handleChange(color as SolidColor)}
            size={isMobile ? 42 : 30}
          />
        </Section.Root>
        {selectedColor.type === 'solidColor' && (
          <Section.Root>
            <Section.Header>
              <Text variant="smallList">Adjust Color</Text>
              <div className={Styles.hexInput}>
                <HexColorInput
                  color={selectedColor.color}
                  aria-label="Hex Value"
                  name="hex"
                  onChange={handleHexInput}
                  size="xs"
                />
              </div>
            </Section.Header>
            <SolidFill
              value={selectedColor}
              onChange={handleChange}
              allowTransparency={allowTransparency}
              margin="0 -4px"
              withHexInput={false}
            />
          </Section.Root>
        )}
      </div>
    )
  }),
)
